import { useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { AccreditationStatusEnumType, InvestmentEntityTypeEnumType } from '__generated__/graphql';
import classNames from 'classnames';
import { FC, useEffect, useRef, useState } from 'react';

import SidebarLayout, {
  SidebarLayoutContent,
  SidebarLayoutNavigation,
} from 'layouts/SidebarLayout';

import AccreditationProcessInfo from 'components/AccreditationProcessInfo';
import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanel, FormPanelWithReadMode } from 'components/FormPanel';

import ConfirmButton from 'primitives/ConfirmButton';
import LoadingIndicator from 'primitives/LoadingIndicator';
import Select from 'primitives/Select';

import {
  AccreditationCriteria,
  BenefitsSection,
} from '../InvestmentEntities/utils/HelperComponents';
import { AccreditationFAQs } from './AccreditationFAQs';
import { getEligibilityOptions } from './EligibilityCriteria';
import JointAccreditationDetailsDialogText from './JointAccreditationDetailsDialogText';

const INVESTMENT_ENTITIES_QUERY = gql(`
  query FetchInvestmentEntitiesForAccreditation {
    dashboard {
      id
      investmentEntities {
        id
        name
        status
        type
        createdAt
        updatedAt
        investorApplication {
          id
          accreditationStatus
          accreditationProofDocument {
            url
            name
          }
          accreditationEligibilityCriteria
        }
      }
      lastUsedInvestmentEntity {
        id
      }
    }
  }
`);

const UPSERT_ACCREDITATION_APPLICATION_MUTATION = gql(`
  mutation UpsertAccreditationApplication(
    $investmentEntityId: ID!
    $accreditationStatus: AccreditationStatusEnumType!
    $accreditationProofDocument: Upload
    $accreditationEligibilityCriteria: [AccreditationEligibilityCriteriaEnumType!]
  ) {
    upsertAccreditationApplication(
      investmentEntityId: $investmentEntityId
      accreditationStatus: $accreditationStatus
      accreditationProofDocument: $accreditationProofDocument
      accreditationEligibilityCriteria: $accreditationEligibilityCriteria
    ) {
      id
      accreditationStatus
      accreditationProofDocument {
        url
        name
      }
      accreditationEligibilityCriteria
    }
  }
`);

const AccreditationPage: FC = () => {
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const [formData, setFormData] = useState<any>({});
  const [submitButtonLabel, setSubmitButtonLabel] = useState('Submit');
  const [investmentEntityId, setInvestmentEntityId] = useState('');

  const { loading, error, data, refetch } = useQuery(INVESTMENT_ENTITIES_QUERY);

  const [upsertAccreditationApplication, { loading: mutationLoading, error: mutationError }] =
    useMutation(UPSERT_ACCREDITATION_APPLICATION_MUTATION, {
      refetchQueries: ['FetchInvestmentEntitiesForAccreditation'],
    });

  useEffect(() => {
    if (formData.accreditationStatus === AccreditationStatusEnumType.InitiateProcess) {
      setSubmitButtonLabel('Help me get Accredited');
    } else if (formData.accreditationStatus === AccreditationStatusEnumType.NotAccredited) {
      setSubmitButtonLabel('Submit');
    }
  }, [formData.accreditationStatus]);

  if (loading) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const investmentEntities = data.dashboard.investmentEntities;

  const hasSubmittedFormPreviously = (investmentEntityId: string) => {
    const investorApplication = investmentEntities.find(
      investmentEntity => investmentEntity.id === investmentEntityId
    )?.investorApplication;

    if (!investorApplication) return false;

    return (
      investorApplication.accreditationStatus !==
      AccreditationStatusEnumType.PendingInvestorResponse
    );
  };

  const getSelectedInvestmentEntity = () => {
    return investmentEntities.find(investmentEntity => investmentEntity.id === investmentEntityId);
  };

  const renderContent = () => {
    return (
      <>
        <SidebarLayoutNavigation
          title="Accreditation"
          subTitle="Become an Accredited Investor today"
        />
        <SidebarLayoutContent>
          <div className="mb-24">
            <h3 className="mb-1 font-bold">Help us understand your accreditation status</h3>
            <h4 className="mb-4 text-sm font-medium text-gray-700">
              Fill out the form below to help us assist you better. Depending on your answers, we
              may need to ask you for additional information.
            </h4>
            <div className="p-8 bg-white border border-gray-200">
              <Select
                label="Investment Entity"
                options={investmentEntities.map(investmentEntity => ({
                  label: investmentEntity?.name,
                  value: investmentEntity?.id,
                  disabled: investmentEntity.status !== 'ACTIVE',
                  labelSecondaryText: investmentEntity.status === 'ACTIVE' ? undefined : 'Inactive',
                }))}
                onChange={value => {
                  setInvestmentEntityId(value);
                }}
                value={investmentEntityId}
                className="w-full"
              />
              {investmentEntityId && hasSubmittedFormPreviously(investmentEntityId) ? (
                getSelectedInvestmentEntity()?.investorApplication?.accreditationStatus ===
                AccreditationStatusEnumType.Accredited ? (
                  <p className="col-span-full text-sm text-green-500 mt-2">
                    We have verified your accreditation details for this investment entity. No
                    further action is required.
                  </p>
                ) : getSelectedInvestmentEntity()?.investorApplication?.accreditationStatus ===
                  AccreditationStatusEnumType.NotAccredited ? (
                  <p className="col-span-full text-sm text-gray-500 mt-2">
                    Thank you for your response. If there is any change in your status, you can
                    resubmit your details.
                  </p>
                ) : (
                  <p className="col-span-full text-sm text-green-500 mt-2">
                    We have received your accreditation details for this investment entity. We will
                    review them and get back to you soon. If you would like, you can resubmit your
                    details.
                  </p>
                )
              ) : null}
              {investmentEntityId ? (
                !hasSubmittedFormPreviously(investmentEntityId) ? (
                  <div className="mt-6">
                    <FormPanel
                      loading={loading || mutationLoading}
                      error={error || mutationError}
                      buttonRef={submitButtonRef}
                      onSubmit={async data => {
                        const accreditationStatus =
                          data.currentlyAccredited === 'YES'
                            ? AccreditationStatusEnumType.PendingAlindiaVerification
                            : data.qualifiesForAccreditation === 'QUALIFY'
                            ? AccreditationStatusEnumType.InitiateProcess
                            : AccreditationStatusEnumType.NotAccredited;
                        try {
                          await upsertAccreditationApplication({
                            variables: {
                              investmentEntityId,
                              accreditationStatus,
                              accreditationProofDocument: data.accreditationProofDocument
                                ? data.accreditationProofDocument
                                : undefined,
                              accreditationEligibilityCriteria:
                                data.accreditationEligibilityCriteria || [],
                            },
                          });
                        } catch (err) {}
                      }}
                      submitButtonLabel="Submit"
                    >
                      <FormInput
                        fullWidth
                        type="radio-group"
                        fieldName="currentlyAccredited"
                        label="Do you currently have a valid Accreditation Certificate issued by a SEBI-approved KYC Registration Agency?"
                        helperText="SEBI-approved KYC Registration Agencies are CDSL Ventures Limited (CVL) and NSDL Database Management Limited (NDML)"
                        defaultValue={''}
                        options={[
                          { value: 'NO', label: 'No' },
                          { value: 'YES', label: 'Yes' },
                        ]}
                        validators={{
                          required: true,
                        }}
                        onChange={value => {
                          setFormData(value);
                        }}
                      />
                      <FormInput
                        fieldName="accreditationProofDocument"
                        label="Accreditation Proof Document *"
                        helperText="Please provide an Accredited Investor (AI) Certificate"
                        type="upload"
                        defaultValue={''}
                        validators={{
                          required: true,
                        }}
                        conditionsToShow={{
                          matches: [
                            {
                              field: 'currentlyAccredited',
                              condition: '===',
                              value: 'YES',
                            },
                          ],
                        }}
                      />
                      <FormInput
                        fullWidth
                        type="radio-group"
                        fieldName="qualifiesForAccreditation"
                        label="Do you qualify (individually or with a joint applicant) to obtain an accreditation certificate?"
                        defaultValue={''}
                        options={[
                          {
                            value: 'DO_NOT_QUALIFY',
                            label: 'No, I do not qualify',
                          },
                          {
                            value: 'QUALIFY',
                            label: 'Yes, I qualify',
                          },
                        ]}
                        validators={{
                          required: true,
                        }}
                        conditionsToShow={{
                          matches: [
                            {
                              field: 'currentlyAccredited',
                              condition: '===',
                              value: 'NO',
                            },
                          ],
                        }}
                        onChange={value => {
                          setFormData(value);
                        }}
                      />
                      <div
                        className={classNames(
                          'my-1 col-span-full',
                          formData.currentlyAccredited === 'NO' ? 'block' : 'hidden',
                          getSelectedInvestmentEntity()?.type ===
                            InvestmentEntityTypeEnumType.Individual
                            ? 'block'
                            : 'hidden'
                        )}
                      >
                        <JointAccreditationDetailsDialogText />
                      </div>
                      <FormInput
                        fullWidth
                        type="checkbox-group"
                        fieldName="accreditationEligibilityCriteria"
                        label="Which of the following criteria do you qualify for?"
                        helperText="You can select multiple options if eligible"
                        defaultValue={''}
                        compact={true}
                        options={getEligibilityOptions(
                          getSelectedInvestmentEntity()?.type as InvestmentEntityTypeEnumType
                        )}
                        validators={{
                          required: true,
                        }}
                        conditionsToShow={{
                          matches: [
                            {
                              field: 'currentlyAccredited',
                              condition: '===',
                              value: 'NO',
                            },
                            {
                              field: 'qualifiesForAccreditation',
                              condition: '===',
                              value: 'QUALIFY',
                            },
                          ],
                        }}
                      />
                      <div className="my-5" />
                      <ConfirmButton
                        onConfirm={() => {
                          if (
                            formData.currentlyAccredited === 'NO' &&
                            formData.qualifiesForAccreditation === 'QUALIFY'
                          ) {
                            window.open(
                              'https://accreditation.navigateaif.co.in/#/sign-up',
                              '_blank'
                            );
                          }
                          // @ts-ignore
                          submitButtonRef.current?.submit();
                        }}
                        confirmButtonLabel={
                          formData.currentlyAccredited === 'NO' &&
                          formData.qualifiesForAccreditation === 'QUALIFY'
                            ? 'Initiate Process'
                            : 'Submit'
                        }
                        title="Submit your accreditation details?"
                        description="You can modify your submission anytime."
                        descriptionComponent={
                          formData.currentlyAccredited === 'NO' &&
                          formData.qualifiesForAccreditation === 'QUALIFY' ? (
                            <AccreditationProcessInfo className="mt-4" />
                          ) : null
                        }
                        className={`col-span-full ${
                          submitButtonLabel === 'Submit' ? 'w-24' : 'w-48'
                        }`}
                      >
                        {submitButtonLabel}
                      </ConfirmButton>
                    </FormPanel>
                  </div>
                ) : getSelectedInvestmentEntity()?.investorApplication?.accreditationStatus ===
                  AccreditationStatusEnumType.Accredited ? (
                  <></>
                ) : (
                  <div className="mt-6">
                    <FormPanelWithReadMode
                      loading={loading || mutationLoading}
                      error={error || mutationError}
                      onSubmit={async formData => {
                        const accreditationStatus =
                          formData.currentlyAccredited === 'YES'
                            ? AccreditationStatusEnumType.PendingAlindiaVerification
                            : formData.qualifiesForAccreditation === 'QUALIFY'
                            ? AccreditationStatusEnumType.InitiateProcess
                            : AccreditationStatusEnumType.NotAccredited;
                        try {
                          await upsertAccreditationApplication({
                            variables: {
                              investmentEntityId,
                              accreditationStatus,
                              accreditationProofDocument:
                                getSelectedInvestmentEntity()?.investorApplication
                                  ?.accreditationProofDocument !==
                                formData.accreditationProofDocument
                                  ? formData.accreditationProofDocument
                                  : undefined,
                              accreditationEligibilityCriteria:
                                formData.accreditationEligibilityCriteria || [],
                            },
                          });
                        } catch (err) {}
                      }}
                      submitButtonLabel="Submit"
                    >
                      <FormInput
                        fullWidth
                        type="radio-group"
                        fieldName="currentlyAccredited"
                        label="Do you currently have a valid Accreditation Certificate issued by a SEBI-approved KYC Registration Agency?"
                        helperText="SEBI-approved KYC Registration Agencies are CDSL Ventures Limited (CVL) and NSDL Database Management Limited (NDML)"
                        defaultValue={
                          getSelectedInvestmentEntity()?.investorApplication
                            ?.accreditationStatus ===
                          AccreditationStatusEnumType.PendingAlindiaVerification
                            ? 'YES'
                            : 'NO'
                        }
                        options={[
                          { value: 'NO', label: 'No' },
                          { value: 'YES', label: 'Yes' },
                        ]}
                        validators={{
                          required: true,
                        }}
                      />
                      <FormInput
                        fieldName="accreditationProofDocument"
                        label="Accreditation Proof Document *"
                        helperText="Please provide an Accredited Investor (AI) Certificate"
                        type="upload"
                        defaultValue={
                          getSelectedInvestmentEntity()?.investorApplication
                            ?.accreditationProofDocument
                        }
                        validators={{
                          required: true,
                        }}
                        conditionsToShow={{
                          matches: [
                            {
                              field: 'currentlyAccredited',
                              condition: '===',
                              value: 'YES',
                            },
                          ],
                        }}
                      />
                      <FormInput
                        fullWidth
                        type="radio-group"
                        fieldName="qualifiesForAccreditation"
                        label="Do you qualify to obtain an accreditation certificate?"
                        defaultValue={
                          getSelectedInvestmentEntity()?.investorApplication
                            ?.accreditationStatus === AccreditationStatusEnumType.NotAccredited
                            ? 'DO_NOT_QUALIFY'
                            : 'QUALIFY'
                        }
                        options={[
                          {
                            value: 'DO_NOT_QUALIFY',
                            label: 'No, I do not qualify',
                          },
                          {
                            value: 'QUALIFY',
                            label: 'Yes, I qualify',
                          },
                        ]}
                        validators={{
                          required: true,
                        }}
                        conditionsToShow={{
                          matches: [
                            {
                              field: 'currentlyAccredited',
                              condition: '===',
                              value: 'NO',
                            },
                          ],
                        }}
                        onChange={value => {
                          setFormData(value);
                        }}
                      />
                      <FormInput
                        fullWidth
                        type="checkbox-group"
                        fieldName="accreditationEligibilityCriteria"
                        label="Which of the following criteria do you qualify for?"
                        helperText="You can select multiple options if eligible"
                        defaultValue={
                          getSelectedInvestmentEntity()?.investorApplication
                            ?.accreditationEligibilityCriteria
                        }
                        compact={true}
                        options={getEligibilityOptions(
                          getSelectedInvestmentEntity()?.type as InvestmentEntityTypeEnumType
                        )}
                        validators={{
                          required: true,
                        }}
                        conditionsToShow={{
                          matches: [
                            {
                              field: 'currentlyAccredited',
                              condition: '===',
                              value: 'NO',
                            },
                            {
                              field: 'qualifiesForAccreditation',
                              condition: '===',
                              value: 'QUALIFY',
                            },
                          ],
                        }}
                      />
                    </FormPanelWithReadMode>
                  </div>
                )
              ) : null}
            </div>

            <div className="my-16">
              <AccreditationCriteria showAtComponent="ACCREDITATION" />
            </div>
            <div className="my-24">
              <BenefitsSection />
            </div>
            <div className="p-4 bg-gray-50 rounded-lg my-24">
              <AccreditationFAQs defaultOpen={true} />
            </div>
          </div>
        </SidebarLayoutContent>
      </>
    );
  };

  return <SidebarLayout>{renderContent()}</SidebarLayout>;
};

export default AccreditationPage;
